import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button, Dialog } from '../ui';
import { wordsOf } from '../todos/todoFormat';
import { api, type MorningAttachPick, type MorningReply } from '../../lib/api';

const NOT_STARTED = "Couldn't write the draft - try again.";
const NOT_SAVED = 'Not saved - try again.';
const NOT_SENT = 'Not sent - try again.';
// ⛔ Must match the 409 words in backend/src/services/mailDraftService.ts (CHANGED) - the button below keys on them.
const CHANGED = 'The draft changed - check it and tap Send again.';
const NOT_SEARCHED = "Couldn't search - try again.";
const NO_DOCS_READ = "Couldn't read the documents - try again.";

const FIELD = 'block w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-[15px] text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/25 disabled:bg-gray-50';
const LABEL = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500';
const ALERT = 'mt-3 break-words rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700';

type Kind = 'deal' | 'master_property';
interface Target { id: string; name: string }
interface Candidate { targetId: string; name: string; detail: string | null }
interface DocRow { id: string; filename: string }

const sizeLabel = (n: number) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
/** Every recipient as he typed them, one line: the confirm names them all. */
const recipients = (to: string) => to.replace(/\s+/g, ' ').trim();

/**
 * MORNING1 5.3 - Reply under an email on the Morning page. Phone first: every field is full width and wraps.
 *  - ⭐ Reply writes a Gmail draft (also in his Gmail Drafts); he edits To, Subject and the message here.
 *  - ⛔ Nothing is attached until he ticks it: the email's own files, or a document FILED on a deal or property he picks.
 *    The suggested deal is listed first, by name - it is a guess, so it attaches nothing.
 *  - ⛔⛔ Every edit is SAVED before Send can ask, and Send carries the version the LAST successful save (or Reply)
 *    returned, so only what he saw goes out. A failed save keeps Send off and shows the server's words.
 *  - ⛔⛔ Only the "Send to ...?" dialog's Send sends. A refusal (409 / 502) shows the server's words; the draft stays.
 */
export default function ReplyPanel({ threadId, onSent, onClose }: {
  threadId: string;
  onSent: (sentTo: string) => void;
  onClose: () => void;
}) {
  const ids = useId();
  const [draft, setDraft] = useState<MorningReply | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [mailPicks, setMailPicks] = useState<string[]>([]);
  const [filePicks, setFilePicks] = useState<DocRow[]>([]);

  // The version Send carries: ALWAYS the latest successful Reply or Save. `dirty` = an edit since then.
  const [version, setVersion] = useState<string | null>(null);
  const [savedTo, setSavedTo] = useState('');
  const [savedNames, setSavedNames] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [confirm, setConfirm] = useState<{ to: string; version: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [kind, setKind] = useState<Kind>('deal');
  const [term, setTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [target, setTarget] = useState<Target | null>(null);
  const [docs, setDocs] = useState<DocRow[] | null>(null);
  const [docsTotal, setDocsTotal] = useState(0);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);

  const start = useCallback(async () => {
    setStarting(true);
    setStartError(null);
    try {
      const d = await api.morning.reply(threadId);
      setDraft(d);
      setTo(d.to);
      setSubject(d.subject);
      setBody(d.body);
      setVersion(d.version);
      setSavedTo(d.to);
      setSavedNames(d.attachments.map((a) => a.name));
      setDirty(false);
    } catch (e) {
      setStartError(wordsOf(e, NOT_STARTED));
    } finally {
      setStarting(false);
    }
  }, [threadId]);
  // ⭐ One draft per open: StrictMode runs an effect twice, and a second run would write a second Gmail draft.
  const startedFor = useRef<string | null>(null);
  useEffect(() => {
    if (startedFor.current === threadId) return;
    startedFor.current = threadId;
    void start();
  }, [threadId, start]);

  const edited = () => setDirty(true);
  const busy = saving || sending;
  // ⛔ No recipient, no Send: the confirm never asks "Send to ?".
  const canSend = !!draft && !!version && !busy && !saveError && recipients(to) !== '';

  const save = async (): Promise<string | null> => {
    if (!draft) return null;
    const attach: MorningAttachPick[] = [
      ...mailPicks.map((attachmentId): MorningAttachPick => ({ kind: 'mail', threadId: draft.threadId, attachmentId })),
      ...filePicks.map((f): MorningAttachPick => ({ kind: 'file', fileId: f.id })),
    ];
    setSaving(true);
    setSaveError(null);
    try {
      const r = await api.morning.saveDraft(draft.draftId, { to, subject, body, attach });
      setVersion(r.version);
      setSavedTo(to);
      setSavedNames(r.attachments.map((a) => a.name));
      setDirty(false);
      return r.version;
    } catch (e) {
      setSaveError(wordsOf(e, NOT_SAVED));
      return null;
    } finally {
      setSaving(false);
    }
  };

  /** Send: saves his edit first (if any), then asks. ⛔ Nothing is sent here. */
  const askToSend = async () => {
    setSendError(null);
    if (dirty) {
      const v = await save();
      if (v) setConfirm({ to: recipients(to), version: v });
      return;
    }
    if (version) setConfirm({ to: recipients(savedTo), version });
  };

  /** ⛔⛔ The dialog's Send - the only place mail leaves. */
  const send = async () => {
    if (!draft || !confirm) return;
    setSending(true);
    try {
      const r = await api.morning.sendDraft(draft.draftId, confirm.version);
      setConfirm(null);
      onSent(r.sentTo);
    } catch (e) {
      setConfirm(null);
      setSendError(wordsOf(e, NOT_SENT));
    } finally {
      setSending(false);
    }
  };

  /** "Use what's on screen": save what is on screen over the Gmail draft, so the next Send carries a version of exactly this. */
  const reload = async () => {
    if (await save()) setSendError(null);
  };

  const search = async () => {
    setSearching(true);
    setSearchError(null);
    try {
      const r = await api.searchFilingTargets(kind, term.trim());
      setCandidates(r.candidates.map((c) => ({ targetId: c.targetId, name: c.name, detail: c.detail })));
    } catch (e) {
      setSearchError(wordsOf(e, NOT_SEARCHED));
    } finally {
      setSearching(false);
    }
  };

  /** A deal or property's FILED documents, to choose from. ⛔ Picking the record attaches nothing. */
  const openTarget = async (t: Target) => {
    setTarget(t);
    setDocs(null);
    setDocsError(null);
    setDocsLoading(true);
    try {
      const r = await api.listLibraryFiles({ targetId: t.id, limit: 200 });
      setDocs((r.files as Array<{ id: string; filename: string }>).map((f) => ({ id: f.id, filename: f.filename })));
      setDocsTotal(r.total);
    } catch (e) {
      setDocsError(wordsOf(e, NO_DOCS_READ));
    } finally {
      setDocsLoading(false);
    }
  };

  const toggleMail = (id: string) => {
    setMailPicks((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    edited();
  };
  const toggleFile = (f: DocRow) => {
    setFilePicks((p) => (p.some((x) => x.id === f.id) ? p.filter((x) => x.id !== f.id) : [...p, f]));
    edited();
  };
  const checkbox = (key: string, label: string, checked: boolean, onChange: () => void) => (
    <li key={key}>
      <label className="flex min-w-0 items-start gap-2 text-sm text-gray-800">
        <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0" checked={checked} disabled={busy} onChange={onChange} />
        <span className="min-w-0 break-words">{label}</span>
      </label>
    </li>
  );

  // Documents he picked from another record stay listed (and ticked) so he can see and untick them.
  const pickedElsewhere = filePicks.filter((f) => !docs?.some((d) => d.id === f.id));

  return (
    <>
      <section aria-label="Reply" className="mt-3 min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4">
        {starting && !draft && <p className="text-sm text-gray-600">Writing the draft...</p>}
        {startError && !draft && (
          <div>
            <p role="alert" className={ALERT}>{startError}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => void start()} isLoading={starting}>Try again</Button>
              <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
            </div>
          </div>
        )}

        {draft && (
          <div className="min-w-0 space-y-3">
            <div>
              <label htmlFor={`${ids}-to`} className={LABEL}>To</label>
              <input id={`${ids}-to`} className={FIELD} value={to} disabled={busy}
                onChange={(e) => { setTo(e.target.value); edited(); }} />
            </div>
            <div>
              <label htmlFor={`${ids}-subject`} className={LABEL}>Subject</label>
              <input id={`${ids}-subject`} className={FIELD} value={subject} disabled={busy}
                onChange={(e) => { setSubject(e.target.value); edited(); }} />
            </div>
            <div>
              <label htmlFor={`${ids}-body`} className={LABEL}>Message</label>
              <textarea id={`${ids}-body`} className={`${FIELD} min-h-[10rem]`} rows={8} value={body} disabled={busy}
                onChange={(e) => { setBody(e.target.value); edited(); }} />
            </div>

            {draft.available.length > 0 && (
              <fieldset className="min-w-0">
                <legend className={LABEL}>From this email</legend>
                <ul className="space-y-1">
                  {draft.available.map((a) => checkbox(`mail:${a.attachmentId}`, `${a.name} (${sizeLabel(a.size)})`,
                    mailPicks.includes(a.attachmentId), () => toggleMail(a.attachmentId)))}
                </ul>
              </fieldset>
            )}

            <div className="min-w-0">
              <Button size="sm" variant="outline" aria-expanded={pickerOpen} onClick={() => setPickerOpen((o) => !o)}>
                Add from a deal
              </Button>
              {pickerOpen && (
                <div role="group" aria-label="Add from a deal" className="mt-2 min-w-0 space-y-2 rounded-lg border border-gray-200 bg-white p-3">
                  {draft.suggestedDealId && (
                    <div>
                      <p className="text-xs text-gray-500">Suggested from the sender - check it is the right deal.</p>
                      <button type="button" className="mt-1 block w-full min-w-0 break-words rounded-lg border border-gray-200 px-3 py-2 text-left text-sm font-medium text-gray-900 hover:border-gray-400"
                        onClick={() => void openTarget({ id: draft.suggestedDealId!, name: draft.suggestedDealName ?? 'Unnamed deal' })}>
                        {draft.suggestedDealName ?? 'Unnamed deal'}
                      </button>
                    </div>
                  )}
                  {draft.suggestionWords && <p className="break-words text-sm text-gray-700">{draft.suggestionWords}</p>}

                  <form className="flex min-w-0 flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (term.trim()) void search(); }}>
                    <div className="min-w-0 basis-full sm:basis-auto">
                      <label htmlFor={`${ids}-kind`} className={LABEL}>Look in</label>
                      <select id={`${ids}-kind`} className={FIELD} value={kind}
                        onChange={(e) => { setKind(e.target.value as Kind); setCandidates(null); }}>
                        <option value="deal">Deals</option>
                        <option value="master_property">Properties</option>
                      </select>
                    </div>
                    <div className="min-w-0 flex-1">
                      <label htmlFor={`${ids}-find`} className={LABEL}>Find a deal or property</label>
                      <input id={`${ids}-find`} className={FIELD} value={term} onChange={(e) => setTerm(e.target.value)} />
                    </div>
                    <Button size="sm" type="submit" variant="outline" disabled={!term.trim()} isLoading={searching}>Find</Button>
                  </form>
                  {searchError && <p role="alert" className={ALERT}>{searchError}</p>}
                  {candidates && candidates.length === 0 && <p className="text-sm text-gray-600">Nothing found by that name.</p>}
                  {candidates && candidates.length > 0 && (
                    <ul className="space-y-1">
                      {candidates.map((c) => (
                        <li key={c.targetId}>
                          <button type="button" className="block w-full min-w-0 break-words rounded-lg border border-gray-200 px-3 py-2 text-left text-sm text-gray-900 hover:border-gray-400"
                            onClick={() => void openTarget({ id: c.targetId, name: c.name })}>
                            <span className="font-medium">{c.name}</span>
                            {c.detail && <span className="block text-xs text-gray-500">{c.detail}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {target && (
                    <fieldset className="min-w-0 border-t border-gray-100 pt-2">
                      <legend className={`${LABEL} break-words`}>{`Documents on ${target.name}`}</legend>
                      {docsLoading && <p className="text-sm text-gray-600">Loading...</p>}
                      {docsError && <p role="alert" className={ALERT}>{docsError}</p>}
                      {docs && docs.length === 0 && <p className="text-sm text-gray-600">{`No documents filed on ${target.name}.`}</p>}
                      {docs && docs.length > 0 && (
                        <ul className="space-y-1">
                          {docs.map((d) => checkbox(`file:${d.id}`, d.filename, filePicks.some((f) => f.id === d.id), () => toggleFile(d)))}
                        </ul>
                      )}
                      {docs && docsTotal > docs.length && (
                        <p className="mt-1 text-xs text-gray-500">{`Showing ${docs.length} of ${docsTotal} documents.`}</p>
                      )}
                    </fieldset>
                  )}
                </div>
              )}
              {pickedElsewhere.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {pickedElsewhere.map((f) => checkbox(`picked:${f.id}`, f.filename, true, () => toggleFile(f)))}
                </ul>
              )}
            </div>

            {savedNames.length > 0 && !dirty && (
              <p className="break-words text-xs text-gray-600">{`Attached: ${savedNames.join(', ')}`}</p>
            )}
            <p className="text-xs text-gray-500">The draft is also in your Gmail Drafts.</p>

            {saveError && <p role="alert" className={ALERT}>{saveError}</p>}
            {sendError && (
              <div role="alert" className={ALERT}>
                <span>{sendError}</span>
                {sendError === CHANGED && (
                  <>
                    <Button size="sm" variant="outline" className="ml-2 mt-1" onClick={() => void reload()} isLoading={saving}>
                      Use what's on screen
                    </Button>
                    <span className="mt-1 block text-xs">This replaces the Gmail draft with what you see here.</span>
                  </>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={() => void askToSend()} disabled={!canSend} isLoading={sending}>Send</Button>
              <Button size="sm" variant="outline" onClick={() => void save()} disabled={!draft || busy} isLoading={saving}>Save draft</Button>
              <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
            </div>
          </div>
        )}
      </section>

      {/* ⛔⛔ The confirm names every recipient; only its Send sends. */}
      <Dialog open={!!confirm} onClose={() => { if (!sending) setConfirm(null); }} size="sm" dismissible={!sending}
        title={confirm ? `Send to ${confirm.to}?` : ''}
        footer={(
          <>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={sending}>Cancel</Button>
            <Button onClick={() => void send()} isLoading={sending}>Send</Button>
          </>
        )}>
        <p className="break-words text-sm text-gray-700">{subject}</p>
      </Dialog>
    </>
  );
}
